/**
 * Dev Fake Auth - local development login bypass (BUG-090 follow-up, 2026-07-10)
 *
 * Lets developers/AI agents get straight into the app on localhost without
 * Google OAuth, so the local dev/test loop (pages, dev tools, sync against
 * the local API) is fast and scriptable.
 *
 * SAFETY - three independent gates, ALL required:
 *   1. import.meta.env.DEV            - true only under `vite dev`. In
 *      production builds this is a compile-time `false`, so the entire
 *      fake path is dead-code-eliminated from the bundle.
 *   2. VITE_DEV_FAKE_AUTH === 'true'  - explicit opt-in via apps/web/.env.local
 *      (comment it out to test the real Google OAuth flow locally).
 *   3. hostname is localhost/127.0.0.1 - never active on LAN/phone testing
 *      or any deployed origin.
 *
 * Server side: the local API accepts the fake user through the legacy
 * userId fallback, enabled by REQUIRE_SYNC_AUTH=false in apps/api/.env.local
 * (LOCAL ONLY - Railway does not set it, so production always enforces JWT).
 *
 * The fake user is an owner + Pro-bypass email so /dev, /log, /metrics_dev
 * and sync-manager paths are all exercisable locally.
 */

import type { Session, User } from '@supabase/supabase-js';

export const DEV_FAKE_USER_ID = 'dev-local-user-00000000';

function isEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  // Vitest runs with DEV=true + jsdom localhost + .env.local loaded, so the
  // other gates all pass in tests - explicitly never fake auth there
  // (31 auth/security tests fail otherwise).
  if (import.meta.env.MODE === 'test') return false;
  if (import.meta.env.VITE_DEV_FAKE_AUTH !== 'true') return false;
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

/** True when the fake local session is active (see gates above). */
export function isDevFakeAuth(): boolean {
  return isEnabled();
}

let cachedSession: Session | null = null;

/**
 * Fake session for local dev. Returns null unless all safety gates pass.
 * Shaped like a real Supabase session; the access_token is a non-JWT
 * placeholder (the local API accepts the userId fallback instead).
 */
export function getDevFakeSession(): Session | null {
  if (!isEnabled()) return null;
  if (cachedSession) return cachedSession;

  const email = import.meta.env.VITE_DEV_FAKE_EMAIL || 'nova223@gmail.com';
  const now = new Date().toISOString();

  const user = {
    id: DEV_FAKE_USER_ID,
    aud: 'authenticated',
    role: 'authenticated',
    email,
    email_confirmed_at: now,
    app_metadata: { provider: 'dev-fake', providers: ['dev-fake'] },
    user_metadata: { full_name: 'Local Dev User' },
    created_at: now,
  } as unknown as User;

  cachedSession = {
    access_token: 'dev-fake-token-not-a-jwt',
    refresh_token: 'dev-fake-refresh',
    token_type: 'bearer',
    expires_in: 3600 * 24 * 365,
    expires_at: Math.floor(Date.now() / 1000) + 3600 * 24 * 365,
    user,
  } as Session;

  console.warn(
    '[AUTH] DEV FAKE AUTH ACTIVE - signed in as',
    email,
    '(localhost dev only; disable via VITE_DEV_FAKE_AUTH in apps/web/.env.local)'
  );
  return cachedSession;
}
